import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ChatService, FormatDatesService, NotificationService, SessionStorageService } from '@shared/services';
import { BulkMessage } from '@shared/factories';
import { MetaModel } from '@shared/models';

const KINDS = [
  { kind: 'room',     label: 'Mitarbeiter',     icon: 'icon-chat',     style: 'bg-light-blue color-blue' },
  { kind: 'location', label: 'Niederlassungen', icon: 'icon-location', style: 'bg-grey color-secondary-grey' },
  { kind: 'company',  label: 'Kunden',          icon: 'icon-case',     style: 'bg-light-orange color-orange' }
];

@Component({
  selector:      'bulk-messages-list',
  templateUrl: './bulk-messages-list.component.html',
  host: { class: 'am-flex-column am-flex-1 height-full overflow relative' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BulkMessagesListComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  messages:       BulkMessage[] = [];
  kinds           = KINDS;
  filterKinds:    string[] = [];
  expandedId:     number;

  search:         string;
  currentPage:    number;
  totalPages:     number;
  meta:           MetaModel;
  loading:        boolean;
  chatFullAccess: boolean;

  @Output() edit   = new EventEmitter<BulkMessage>();
  @Output() create = new EventEmitter<void>();
  constructor(
    private chatService:           ChatService,
    private sessionStorageService: SessionStorageService,
    private notificationService:   NotificationService,
  ) { }

  ngOnInit(): void {
    this.sessionStorageService.searchSecondaryValue.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(search => {
      if      (search?.useCase === 'bulkMessages') this.search = search.query;
      else if (!search && this.search)           { this.search = null; this.loadBulkMessages(); }
    });
    this.sessionStorageService.chatFullAccess.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(access => this.chatFullAccess = access);
    this.loadBulkMessages();
  }

  ngOnDestroy(): void {
    if (this.sessionStorageService.searchSecondaryValueValue?.useCase === 'bulkMessages') this.sessionStorageService.changeSearchSecondaryValue(null);
  }

  loadBulkMessages(page: number = 1): void {
    this.loading = true;
    this.chatService.loadBulkMessages(page, this.search, this.filterKinds).subscribe(
      ({ messages, totalPages, meta }) => {
        this.messages    = messages;
        this.totalPages  = totalPages;
        this.meta        = meta;
        this.currentPage = page;
        this.loading     = null;
      },
      err => {
        this.notificationService.alert(err);
        this.loading = null;
      }
    );
  }

  toggleKind(kind: string): void {
    if      (!kind)                          this.filterKinds = [];
    else if (this.filterKinds.includes(kind)) this.filterKinds = this.filterKinds.filter(k => k !== kind);
    else                                     this.filterKinds = [...this.filterKinds, kind];
    this.loadBulkMessages();
  }

  kindOf(message: BulkMessage) {
    return KINDS.find(k => k.kind === message.kind) || KINDS[0];
  }

  timeLabel(message: BulkMessage): string {
    return FormatDatesService.timeAgo(message?.createdAt);
  }

  visibleRecipients(message: BulkMessage): string[] {
    return this.expandedId === message.id ? message.recipientNames : message.recipientNames.slice(0, 3);
  }

  hiddenRecipients(message: BulkMessage): number {
    return this.expandedId === message.id ? 0 : Math.max(0, message.recipientNames.length - 3);
  }

  toggle(message: BulkMessage): void {
    this.expandedId = this.expandedId === message.id ? null : message.id;
  }

  deleteMessage(message: BulkMessage): void {
    this.notificationService.confirm(
      'Warnung',
      'Möchten Sie diese Nachricht wirklich löschen',
      () => this.chatService.deleteBulkMessage(message.id).subscribe(
        ()  => this.loadBulkMessages(),
        err => this.notificationService.alert(err)
      )
    );
  }

}
